/* eslint-disable react-hooks/immutability */
import { useState, useEffect, useRef } from "react";
import { trackFunnel } from "../lib/funnel";
import { NonRefundableConfirmModal } from "../components/NonRefundableConfirmModal";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useMode } from "../context/ModeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Crown, Shield, XCircle, Tag, X, Bitcoin, Rocket, Star, AlertTriangle } from "lucide-react";
import { CryptoPaymentModal } from "../components/CryptoPaymentModal";
import { WalletPaymentButtons } from "../components/WalletPaymentButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionPage = () => {
  const { user, refreshUser, marketingMode, getDisplayTier } = useAuth();
  const { mode, config: modeConfig } = useMode();
  const [plans, setPlans] = useState([]);
  const [paypalPlans, setPaypalPlans] = useState({});
  const [paypalClientId, setPaypalClientId] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [couponInput, setCouponInput] = useState(() => {
    // Pre-fill from `?coupon=XXX` URL param (used by influencer drops).
    // Lazy initializer so we don't trigger a re-render via useEffect.
    try {
      return new URLSearchParams(window.location.search).get("coupon")?.toUpperCase() || "";
    } catch {
      return "";
    }
  });
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [cryptoOpen, setCryptoOpen] = useState(false);
  // Non-refundable confirmation gate — set the "pending action" then open
  // the modal. The user must check the box + click Agree before the actual
  // payment flow runs.
  const [refundConfirm, setRefundConfirm] = useState({ open: false, run: null, label: "" });
  // PayPal SDK renders a button that we can't intercept onClick — instead
  // we hide it behind a consent gate until the user has agreed to the
  // non-refundable policy. Reset whenever the selected plan changes.
  const [paypalConsented, setPaypalConsented] = useState(false);
  // Crypto-payments master switch — driven by the backend availability flag.
  // When OKX addresses are unconfigured (or admin has forced-off via
  // CRYPTO_PAYMENTS_ENABLED=false), this is false and we replace the
  // "Pay with Crypto" button with a "Coming back soon" disabled pill.
  const [cryptoEnabled, setCryptoEnabled] = useState(true);
  const paypalButtonRef = useRef(null);
  const paypalScriptLoaded = useRef(false);

  // Use display tier (respects marketing mode)
  const displayTier = getDisplayTier();

  useEffect(() => {
    // One-time setup only — anything mode-independent.
    axios.post(`${API}/subscriptions/viewed`).catch(() => { /* best-effort */ });
    axios.get(`${API}/payments/crypto/availability`).then((r) => {
      setCryptoEnabled(r.data?.enabled !== false);
    }).catch(() => { /* default to enabled on network error */ });
    trackFunnel("plan_viewed");
  }, []);

  // Fetch plans on mount AND whenever the active studio mode flips so the
  // feature list + AI-model labels match the active section. Same prices.
  useEffect(() => {
    fetchData(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (selectedPlan && paypalClientId && paypalPlans[selectedPlan.id]) {
      loadPayPalButton();
    }
    // Reset the non-refundable consent each time the user picks a new
    // plan — they must re-agree per purchase, not once per session.
    setPaypalConsented(false);
  }, [selectedPlan, paypalClientId, paypalPlans]);

  const fetchData = async (modeOverride) => {
    try {
      // Pass is_admin flag to get Ultimate tier if admin + the active studio
      // mode so the plan features + AI-model labels match what the user sees.
      const activeMode = modeOverride || mode;
      const params = new URLSearchParams();
      if (user?.is_admin) params.set("is_admin", "true");
      if (activeMode) params.set("mode", activeMode);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [plansRes, paypalRes, statusRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans${qs}`),
        axios.get(`${API}/payments/paypal-plans`),
        axios.get(`${API}/payments/subscription-status`)
      ]);

      setPlans(plansRes.data.plans);
      setPaypalPlans(paypalRes.data.plans || {});
      setPaypalClientId(paypalRes.data.client_id);
      setSubscriptionStatus(statusRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load subscription plans");
    } finally {
      setLoading(false);
    }
  };

  const loadPayPalButton = () => {
    // Clear previous button
    if (paypalButtonRef.current) {
      paypalButtonRef.current.innerHTML = "";
    }

    // Load PayPal script if not loaded
    if (!window.paypal && !paypalScriptLoaded.current) {
      paypalScriptLoaded.current = true;
      const script = document.createElement("script");
      script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&vault=true&intent=subscription`;
      script.setAttribute("data-sdk-integration-source", "button-factory");
      script.onload = () => renderPayPalButton();
      document.body.appendChild(script);
    } else if (window.paypal) {
      renderPayPalButton();
    }
  };

  const renderPayPalButton = () => {
    if (!window.paypal || !paypalButtonRef.current || !selectedPlan) return;

    const planId = paypalPlans[selectedPlan.id];
    if (!planId) return;

    window.paypal.Buttons({
      style: {
        shape: "pill",
        color: "gold",
        layout: "vertical",
        label: "subscribe"
      },
      createSubscription: (data, actions) => {
        return actions.subscription.create({
          plan_id: planId
        });
      },
      onApprove: async (data) => {
        try {
          await axios.post(`${API}/payments/subscribe`, {
            subscription_id: data.subscriptionID,
            tier: selectedPlan.id,
            coupon_code: coupon?.code || null,
          });
          if (coupon?.bonus_tokens) {
            toast.success(`Subscribed to ${selectedPlan.name}! +${coupon.bonus_tokens} bonus tokens from ${coupon.code}.`);
          } else {
            toast.success(`Successfully subscribed to ${selectedPlan.name}!`);
          }
          refreshUser();
          setSelectedPlan(null);
          setCoupon(null); setCouponInput("");
          fetchData();
        } catch (error) {
          toast.error("Failed to activate subscription");
        }
      },
      onError: (err) => {
        console.error("PayPal error:", err);
        toast.error("Payment failed. Please try again.");
      }
    }).render(paypalButtonRef.current);
  };

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code || !selectedPlan) return;
    setCouponLoading(true);
    try {
      const r = await axios.post(`${API}/coupons/validate`, {
        code, tier: selectedPlan.id,
      });
      setCoupon(r.data);
      toast.success(`${code} applied — +${r.data.bonus_tokens} bonus tokens on subscribe`);
    } catch (e) {
      setCoupon(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    } finally { setCouponLoading(false); }
  };

  const handleClearCoupon = () => {
    setCoupon(null); setCouponInput("");
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You'll be moved to the Free plan.")) {
      return;
    }

    setCancelling(true);
    try {
      await axios.post(`${API}/payments/cancel-subscription`);
      toast.success("Subscription cancelled");
      refreshUser();
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleSelectFreePlan = async () => {
    if (user?.subscription_tier === "free") {
      toast.info("You're already on the Free plan");
      return;
    }

    // If they have an active subscription, they need to cancel first
    if (subscriptionStatus?.has_subscription) {
      toast.info("Please cancel your current subscription first");
      return;
    }

    try {
      await axios.post(`${API}/subscriptions/upgrade`, { tier: "free" });
      toast.success("Switched to Free plan");
      refreshUser();
    } catch (error) {
      toast.error("Failed to switch plan");
    }
  };

  const planIcons = {
    free: Zap,
    starter: Rocket,
    premium: Sparkles,
    pro: Star,
    platinum: Crown,
    ultimate: Shield,
  };

  const planColors = {
    free: {
      gradient: "from-slate-600 to-slate-700",
      border: "border-ink-200",
      text: "text-ink-700"
    },
    starter: {
      gradient: "from-sky-500 to-sky-600",
      border: "border-sky-500",
      text: "text-sky-700",
    },
    premium: {
      gradient: "from-emerald-600 to-emerald-700",
      border: "border-ink-950",
      text: "text-ink-700"
    },
    pro: {
      gradient: "from-violet-500 to-violet-700",
      border: "border-violet-500",
      text: "text-violet-700",
    },
    platinum: {
      gradient: "from-amber-500 to-amber-600",
      border: "border-amber-500",
      text: "text-amber-300"
    },
    ultimate: {
      gradient: "from-purple-600 to-pink-600",
      border: "border-purple-500",
      text: "text-purple-300"
    }
  };

  // Check if user is on ultimate tier (locked) - but hide if marketing mode is on
  const isUltimateTier = user?.subscription_tier === "ultimate" && !marketingMode;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ink-950"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="subscription-page">
      {/* Header - Fancy for Ultimate */}
      {isUltimateTier ? (
        <div className="text-center relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-500/20 to-amber-500/20 blur-3xl -z-10"></div>
          <h1 className="text-3xl lg:text-4xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent drop-shadow-lg">
              ULTIMATE
            </span>
            <span className="text-ink-950 ml-2">Plan</span>
          </h1>
          <p className="text-purple-300 mt-2 text-lg">
            ✨ The most exclusive tier with unlimited power ✨
          </p>
        </div>
      ) : (
        <div className="text-center" data-testid={`subscription-header-${mode}`}>
          <p
            className="text-xs font-black uppercase tracking-[0.22em] mb-2"
            style={{ color: modeConfig?.accent }}
          >
            {modeConfig?.label || "Marketing"} plans
          </p>
          <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight">
            Choose Your Plan
          </h1>
          <p className="text-ink-500 mt-2">
            One subscription, all three studios. {modeConfig?.tagline || ""}
          </p>
        </div>
      )}

      {/* Current Plan Banner - Super Fancy for Ultimate */}
      {isUltimateTier ? (
        <div className="relative overflow-hidden p-6 rounded-2xl bg-gradient-to-r from-purple-900/80 via-pink-900/80 to-purple-900/80 border-2 border-amber-500/50 shadow-2xl shadow-purple-500/30">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyem0wLTRWMjhIMjR2Mmgxem0tMTItNmgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTEyIDBoMnYyaC0ydi0yem0wIDRoMnYyaC0ydi0yeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/50">
                <Crown className="w-6 h-6 text-ink-950" />
              </div>
              <div>
                <p className="text-lg font-bold bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
                  ULTIMATE PLAN
                </p>
                <p className="text-purple-300 text-sm">Admin Exclusive • Permanently Locked</p>
              </div>
            </div>
            <div className="px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/50">
              <span className="text-amber-400 font-bold text-sm">🔒 LIFETIME ACCESS</span>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="p-4 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4 border"
          style={{
            backgroundColor: `${modeConfig?.accent || "#FF4F27"}1A`,
            borderColor: `${modeConfig?.accent || "#FF4F27"}33`,
          }}
          data-testid="current-plan-banner"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5" style={{ color: modeConfig?.accent }} />
            <p className="text-ink-700">
              Current Plan: <span className="font-semibold capitalize">{displayTier}</span>
            </p>
          </div>
          
          {subscriptionStatus?.has_subscription && (
            <div className="flex items-center gap-4">
              {subscriptionStatus.next_billing && (
                <p className="text-sm text-ink-500">
                  Next billing: {new Date(subscriptionStatus.next_billing).toLocaleDateString()}
                </p>
              )}
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                variant="outline"
                size="sm"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 gap-2"
                data-testid="cancel-subscription-btn"
              >
                <XCircle className="w-4 h-4" />
                {cancelling ? "Cancelling..." : "Cancel Subscription"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Ultimate Tier - Super Fancy Card for admin */}
      {isUltimateTier ? (
        <div className="max-w-lg mx-auto">
          <div className="relative p-8 rounded-3xl overflow-hidden">
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-purple-900"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400"></div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400"></div>
            
            {/* Sparkle effects */}
            <div className="absolute top-4 right-4 w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
            <div className="absolute top-8 right-12 w-1 h-1 bg-pink-400 rounded-full animate-pulse delay-100"></div>
            <div className="absolute top-12 right-6 w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse delay-200"></div>
            
            <div className="relative z-10">
              {/* Admin Exclusive Badge */}
              <div className="flex justify-center mb-6">
                <div className="px-6 py-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/50">
                  <span className="text-ink-950 font-bold text-sm tracking-wider">👑 ADMIN EXCLUSIVE 👑</span>
                </div>
              </div>
              
              {/* Plan Icon & Name */}
              <div className="text-center mb-8">
                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-amber-500/50 border-4 border-amber-300/30">
                  <Crown className="w-12 h-12 text-ink-950 drop-shadow-lg" />
                </div>
                <h3 className="text-4xl font-black bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent mb-2">
                  ULTIMATE
                </h3>
                <div className="mt-4">
                  <span className="text-5xl font-black text-ink-950">FREE</span>
                  <span className="text-purple-300 text-xl ml-2">forever</span>
                </div>
              </div>

              {/* AI Model Badge */}
              <div className="text-center mb-6 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/30">
                <p className="text-sm text-purple-200">
                  🤖 AI Model: <span className="font-bold text-ink-950">Gemini 3 Flash + Nano Banana</span>
                </p>
              </div>

              {/* Features List */}
              <ul className="space-y-4 mb-8">
                {[
                  { text: "1000 tokens/month (unlimited generation)", highlight: true },
                  { text: "~100 videos or 200 images", highlight: true },
                  { text: "AI content generation (Gemini 3 Flash)", highlight: false },
                  { text: "AI image enhancement", highlight: false },
                  { text: "AI auto-generated hashtags", highlight: true },
                  { text: "Auto Instagram Stories on every post", highlight: false },
                  { text: "All premium features included", highlight: false },
                  { text: "Cannot be downgraded", highlight: true }
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      feature.highlight 
                        ? "bg-gradient-to-r from-amber-400 to-amber-600" 
                        : "bg-purple-500/50"
                    }`}>
                      <Check className="w-4 h-4 text-ink-950" />
                    </div>
                    <span className={`text-base ${
                      feature.highlight 
                        ? "text-amber-300 font-semibold" 
                        : "text-purple-100"
                    }`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Footer Note */}
              <div className="text-center p-4 rounded-xl bg-black/30 border border-amber-500/20">
                <p className="text-amber-400 font-medium">
                  🔐 This plan is permanently locked to your admin account
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Plans Grid - Regular users */
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {plans.filter(plan => !plan.admin_only).map((plan) => {
            const PlanIcon = planIcons[plan.id] || Zap;
            const colors = planColors[plan.id] || planColors.free;
            const isCurrentPlan = user?.subscription_tier === plan.id;
            const isSelected = selectedPlan?.id === plan.id;
            const isPopular = plan.id === "pro";

            return (
              <div
                key={plan.id}
                onClick={() => {
                  if (!isCurrentPlan) {
                    if (plan.id === "free") {
                      handleSelectFreePlan();
                    } else {
                      setSelectedPlan(plan);
                      setCoupon(null); setCouponInput("");
                    }
                  }
                }}
                className={`relative p-6 rounded-2xl border transition-all duration-300 cursor-pointer ${
                  isSelected
                    ? "ring-2 ring-ink-950 border-ink-950"
                    : isPopular 
                      ? `bg-gradient-to-b ${colors.gradient} border-transparent scale-105 shadow-2xl shadow-emerald-500/20` 
                      : `bg-white ${colors.border} hover:border-ink-300`
                } ${isCurrentPlan ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={`plan-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-white text-emerald-600 text-xs font-bold">
                    MOST POPULAR
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-coral-500 text-ink-950 text-xs font-bold">
                    CURRENT
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center mx-auto mb-4`}>
                    <PlanIcon className="w-7 h-7 text-ink-950" />
                  </div>
                  <h3 className="text-xl font-bold text-ink-950">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-4xl font-bold text-ink-950">${plan.price}</span>
                    <span className={isPopular ? "text-ink-700" : "text-ink-500"}>/month</span>
                  </div>
                </div>

                {/* AI Model Badge */}
                <div className={`text-center mb-4 px-3 py-2 rounded-lg ${isPopular ? 'bg-white/10' : 'bg-ink-100'}`}>
                  <p className={`text-sm ${isPopular ? 'text-ink-700' : colors.text}`}>
                    AI Model: <span className="font-semibold text-ink-950">{plan.ai_model}</span>
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 ${isPopular ? 'text-ink-950' : 'text-coral-500'}`} />
                      <span className={`text-sm ${isPopular ? 'text-ink-700' : 'text-ink-700'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Select Button for Free Plan */}
                {plan.id === "free" && !isCurrentPlan && (
                  <Button
                    className="w-full bg-ink-200 hover:bg-ink-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectFreePlan();
                    }}
                  >
                    Select Free Plan
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* PayPal Button Container */}
      {selectedPlan && selectedPlan.price > 0 && !isUltimateTier && (
        <div className="max-w-md mx-auto">
          <div className="p-6 rounded-2xl bg-white border border-ink-200">
            <h3 className="text-lg font-semibold text-ink-950 text-center mb-4">
              Subscribe to {selectedPlan.name} - ${selectedPlan.price}/month
            </h3>
            
            <div className="mb-4 p-3 rounded-lg bg-ink-100 text-center">
              <p className="text-sm text-ink-700">
                ✓ Auto-renews monthly • ✓ Cancel anytime
              </p>
            </div>

            {/* Coupon code input */}
            <div className="mb-4" data-testid="coupon-section">
              {!coupon ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="Coupon code (optional)"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    className="flex-1"
                    data-testid="coupon-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleApplyCoupon}
                    disabled={!couponInput.trim() || couponLoading}
                    className="rounded-full"
                    data-testid="apply-coupon-btn"
                  >
                    <Tag className="w-4 h-4 mr-1" />
                    {couponLoading ? "…" : "Apply"}
                  </Button>
                </div>
              ) : (
                <div
                  className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200"
                  data-testid="coupon-applied"
                >
                  <div className="text-sm">
                    <span className="font-semibold text-emerald-700">{coupon.code}</span>
                    <span className="text-emerald-700"> · +{coupon.bonus_tokens} bonus tokens</span>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Charged ${coupon.base_price.toFixed(2)} via PayPal. Discount delivered as bonus tokens.
                    </p>
                  </div>
                  <button
                    onClick={handleClearCoupon}
                    className="text-emerald-700 hover:text-emerald-900"
                    data-testid="clear-coupon-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Apple Pay / Google Pay (shown only when enabled + device-eligible) */}
            <WalletPaymentButtons
              plan={selectedPlan?.id}
              onSuccess={async () => {
                await refreshUser();
                setSelectedPlan(null);
                fetchData();
              }}
            />

            {/* PayPal Button renders here — visually gated behind the
                non-refundable consent until the user has agreed. */}
            <div className="relative min-h-[150px]" data-testid="paypal-container">
              <div ref={paypalButtonRef} id="paypal-button-container" className={paypalConsented ? "min-h-[150px]" : "min-h-[150px] pointer-events-none opacity-30 blur-[1.5px]"}></div>
              {!paypalConsented && (
                <div
                  className="absolute inset-0 flex items-center justify-center z-10"
                  data-testid="paypal-consent-gate"
                >
                  <Button
                    type="button"
                    onClick={() =>
                      setRefundConfirm({
                        open: true,
                        label: `${selectedPlan?.name || "Subscription"} · pay with PayPal`,
                        run: () => setPaypalConsented(true),
                      })
                    }
                    data-testid="paypal-consent-btn"
                    className="h-12 px-6 bg-ink-950 hover:bg-coral-500 text-white text-xs font-bold uppercase tracking-[0.18em] gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 text-rose-400" />
                    Enable PayPal · accept non-refundable
                  </Button>
                </div>
              )}
            </div>

            {/* OR — Pay with Crypto (USDT/USDC on Polygon, BSC, or Tron).
                Hidden behind a "Coming back soon" pill when the backend
                reports the master switch is off. */}
            <div className="relative my-3 flex items-center">
              <span className="flex-grow border-t border-ink-200"></span>
              <span className="mx-3 text-[10px] font-bold uppercase tracking-[0.22em] text-ink-400">
                or
              </span>
              <span className="flex-grow border-t border-ink-200"></span>
            </div>
            {cryptoEnabled ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    trackFunnel("method_selected", {
                      tier: selectedPlan?.id,
                      method: "crypto",
                    });
                    setRefundConfirm({
                      open: true,
                      label: `${selectedPlan?.name || "Subscription"} · pay with crypto`,
                      run: () => setCryptoOpen(true),
                    });
                  }}
                  data-testid="pay-with-crypto-btn"
                  className="group w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-ink-950 hover:bg-coral-500 text-white font-display font-bold text-sm tracking-tight transition-colors"
                >
                  <Bitcoin className="w-4 h-4 text-citrus-500 group-hover:text-white transition-colors" />
                  Pay with Crypto · USDT / USDC
                </button>
                <p className="text-[10px] text-ink-500 text-center mt-1.5 leading-relaxed">
                  One-time 30-day activation · Polygon · BSC · Tron
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  data-testid="pay-with-crypto-btn-disabled"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-ink-100 border border-ink-200 text-ink-500 font-display font-bold text-sm tracking-tight cursor-not-allowed"
                >
                  <Bitcoin className="w-4 h-4 text-amber-500" />
                  Crypto · Coming back soon
                </button>
                <p className="text-[10px] text-ink-500 text-center mt-1.5 leading-relaxed">
                  We&apos;re finishing setup on deposit addresses. Use PayPal in the meantime.
                </p>
              </>
            )}

            <Button
              variant="ghost"
              className="w-full mt-4 text-ink-500"
              onClick={() => setSelectedPlan(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Note */}
      {!isUltimateTier && (
        <div className="text-center text-sm text-ink-500 max-w-2xl mx-auto">
          <p>
            All plans include access to Instagram and TikTok content generation.
            Payments are securely processed by PayPal or settled on-chain to our OKX wallet. Cancel anytime from this page.
          </p>
        </div>
      )}

      {/* Crypto payment modal */}
      <CryptoPaymentModal
        open={cryptoOpen}
        plan={selectedPlan?.id}
        onClose={() => setCryptoOpen(false)}
        onSuccess={async () => { await refreshUser(); setSelectedPlan(null); }}
      />

      {/* Non-refundable payment confirmation — gates every payment action */}
      <NonRefundableConfirmModal
        open={refundConfirm.open}
        onOpenChange={(open) => setRefundConfirm((s) => ({ ...s, open }))}
        onConfirm={() => refundConfirm.run?.()}
        itemLabel={refundConfirm.label}
      />
    </div>
  );
};

export default SubscriptionPage;
